import { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Skeleton } from 'antd';
import BillingModeSelector from 'Pages/Faturamento/Components/BillingModeSelector';
import RateioEditor from 'Pages/Faturamento/Rateio/components/RateioEditor';
import { RateioScopeProvider } from 'Hooks/faturamento/RateioScope';
import { useQueryBillingSplit } from 'Hooks/faturamento/useQueryBillingSplit';
import { useMutationBillingMode } from 'Hooks/faturamento/useMutationBillingMode';
import { isRateioMocked } from 'Data/adapters/Rateio';
import { BillingModeEnum, IRateioShareResponse } from 'Data/interfaces/Faturamento/IRateio';
import { ISpeResponse } from 'Data/interfaces/Faturamento/ISpe';
import { numberToMoney } from 'Utils/Money';
import {
  Block,
  Columns,
  DetailsSection,
  Notice,
  SectionTitle,
  Share,
  SpeCard,
  SpeHeader,
  Tag,
} from './styles';

interface IRateioTab {
  tenantId: number;
  planPrice?: number;
}

const round2 = (value: number): number => Math.round(value * 100) / 100;
const formatWeight = (weight: number): string =>
  `${weight.toFixed(2).replace('.', ',')}%`;

// Aba "Rateio entre SPEs" da modal de Dados de Faturamento do /adm.
//
// A equipe Coordly consulta o rateio para conduzir a emissão das notas no Conta
// Azul (12273) e também o configura pelo cliente — modo, SPEs e pesos (12501) —,
// porque muitos clientes não conseguem preencher sozinhos. O editor e o formulário
// de SPE são os mesmos da tela do cliente; o RateioScopeProvider faz com que eles
// operem no tenant selecionado. O backend grava cada alteração como feita pela
// plataforma.
//
// O antd só monta a aba quando ela é aberta pela primeira vez, então o rateio só é
// buscado se alguém de fato clicar nela.
const RateioTab: FC<IRateioTab> = ({ tenantId, planPrice }) => (
  <RateioScopeProvider tenantId={tenantId} planPrice={planPrice}>
    <RateioTabContent planPrice={planPrice} />
  </RateioScopeProvider>
);

const RateioTabContent: FC<{ planPrice?: number }> = ({ planPrice }) => {
  const { t } = useTranslation(['faturamento', 'common']);
  const { billingSplit, isLoading, error } = useQueryBillingSplit();
  const { setBillingMode, isLoading: isChangingMode } = useMutationBillingMode();

  const item = (label: string, value?: ReactNode) => (
    <>
      <dt>{label}</dt>
      <dd>{value || t('faturamento:admRateio.emptyValue')}</dd>
    </>
  );

  const renderSpe = (spe: ISpeResponse, share?: IRateioShareResponse) => {
    const cityState = [spe.City, spe.State].filter(Boolean).join(' / ');
    const street = [spe.StreetAddress, spe.Number, spe.Complement]
      .filter(Boolean)
      .join(', ');

    return (
      <SpeCard key={spe.SpeId}>
        <SpeHeader>
          <strong>{spe.CompanyName}</strong>
          {share ? (
            <Share>
              {formatWeight(share.Weight)}
              {planPrice
                ? ` · ${numberToMoney(round2((planPrice * share.Weight) / 100), 'real')}`
                : ''}
              {share.IsRoundingAdjusted ? (
                <Tag>{t('faturamento:admRateio.roundingAdjusted')}</Tag>
              ) : null}
            </Share>
          ) : null}
        </SpeHeader>

        <Columns>
          <Block>
            <SectionTitle>{t('faturamento:admRateio.sectionFiscal')}</SectionTitle>
            {item(t('faturamento:spe.fieldCnpj'), spe.CNPJ)}
            {item(t('faturamento:spe.fieldOwnerName'), spe.OwnerName)}
            {item(t('common:phone'), spe.Phone)}
            {item(t('common:email'), spe.Email)}
            {item(t('common:site'), spe.Site)}
          </Block>
          <Block>
            <SectionTitle>{t('faturamento:spe.sectionAddress')}</SectionTitle>
            {item(t('faturamento:spe.fieldStreetAddress'), street)}
            {item(t('faturamento:spe.fieldDistrict'), spe.District)}
            {item(t('faturamento:admRateio.cityState'), cityState)}
            {item(t('faturamento:spe.fieldZip'), spe.Zip)}
          </Block>
          <Block>
            <SectionTitle>{t('faturamento:spe.sectionRepresentative')}</SectionTitle>
            {item(t('faturamento:spe.fieldRepresentativeName'), spe.LegalRepresentative?.Name)}
            {item(t('faturamento:spe.fieldRepresentativeCpf'), spe.LegalRepresentative?.CPF)}
            {item(t('faturamento:spe.fieldRepresentativeRole'), spe.LegalRepresentative?.Role)}
            {item(t('common:email'), spe.LegalRepresentative?.Email)}
            {item(t('common:phone'), spe.LegalRepresentative?.Phone)}
          </Block>
        </Columns>
      </SpeCard>
    );
  };

  const renderContent = () => {
    if (isLoading) return <Skeleton active />;

    if (error || !billingSplit) {
      return <Notice $warning>{t('faturamento:admRateio.loadError')}</Notice>;
    }

    const isSplit =
      billingSplit.BillingMode === BillingModeEnum.SpeSplit && !!billingSplit.Rateio;
    const spes = billingSplit.Spes;

    return (
      <>
        <BillingModeSelector
          value={billingSplit.BillingMode}
          disabled={isChangingMode}
          onChange={setBillingMode}
        />

        {isSplit ? (
          <RateioEditor embedded />
        ) : (
          <Notice>
            {t('faturamento:admRateio.singleCompany')}
            {spes.length > 0
              ? ` ${t('faturamento:admRateio.spesOutsideSplit', { count: spes.length })}`
              : ''}
          </Notice>
        )}

        {spes.length > 0 ? (
          <DetailsSection>
            <SectionTitle>{t('faturamento:admRateio.speDetailsTitle')}</SectionTitle>
            {spes.map((spe) =>
              renderSpe(
                spe,
                isSplit
                  ? billingSplit.Rateio?.Shares.find((share) => share.SpeId === spe.SpeId)
                  : undefined
              )
            )}
          </DetailsSection>
        ) : null}
      </>
    );
  };

  return (
    <>
      <Notice>{t('faturamento:admRateio.editNotice')}</Notice>
      {isRateioMocked() ? (
        <Notice $warning>{t('faturamento:admRateio.mockNotice')}</Notice>
      ) : null}
      {renderContent()}
    </>
  );
};

export default RateioTab;
